import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  LayoutDashboard, Clock, Users, Heart, CreditCard,
  FileText, Settings, UserCog, LogOut, Briefcase, Upload,
  Download, Mail, PanelLeftClose, PanelLeft, Inbox
} from 'lucide-react';
import toast from 'react-hot-toast';
import { exportBackup, getJrsStatus, initiateJrs, getRequestsCount } from '../api/client';
import Modal from './Modal';

const NAV = [
  { sectionKey: 'nav_general', items: [
    { to:'/dashboard', icon:LayoutDashboard, labelKey:'nav_dashboard', roles:['admin','director','teacher','accountant','office'] },
  ]},
  { sectionKey: 'nav_students', items: [
    { to:'/waiting',  icon:Clock,  labelKey:'nav_waiting',       roles:['admin','director','teacher','office','parent'] },
    { to:'/students', icon:Users,  labelKey:'nav_activeStudents', roles:['admin','director','teacher','office','parent'] },
    { to:'/families', icon:Heart,  labelKey:'nav_families',       roles:['admin','director','office','parent'] },
  ]},
  { sectionKey: 'nav_finance', items: [
    { to:'/payments', icon:CreditCard, labelKey:'nav_payments', roles:['admin','director','accountant','parent'] },
    { to:'/but',      icon:Briefcase,  labelKey:'nav_but',      roles:['admin','director','accountant'] },
  ]},
  { sectionKey: 'nav_reports', items: [
    { to:'/reports', icon:FileText, labelKey:'nav_reports', roles:['admin','director','accountant'] },
  ]},
  { sectionKey: 'nav_system', items: [
    { to:'/antraege',    icon:Inbox,    labelKey:'nav_antraege',    roles:['admin','director','office'] },
    { to:'/customizing', icon:Settings, labelKey:'nav_customizing', roles:['admin'] },
    { to:'/users',       icon:UserCog,  labelKey:'nav_users',       roles:['admin'] },
    { to:'/import',      icon:Upload,   labelKey:'nav_import',      roles:['admin'] },
  ]},
];

function JrsModal({ onClose }) {
  const { t } = useLanguage();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getJrsStatus();
      setList(r.data);
    } catch { toast.error(t('error')); }
    finally { setLoading(false); }
  };

  const handleInitiate = async () => {
    if (!window.confirm(t('jrs_newConfirm'))) return;
    try {
      await initiateJrs();
      toast.success(t('jrs_started'));
      load();
    } catch { toast.error(t('error')); }
  };

  React.useEffect(() => { load(); }, []);

  const emails = list ? list.map(f => f.email).filter(Boolean).join('; ') : '';
  const confirmed = list ? list.filter(f => f.jrs_confirmed).length : 0;

  return (
    <Modal title={t('jrs_title')} onClose={onClose}>
      <div style={{background:'var(--bg2)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:13,marginBottom:14,color:'var(--muted)'}}>
        {t('jrs_info')}
      </div>
      {list && (
        <>
          <div className="fg" style={{marginBottom:14}}>
            <label style={{fontWeight:600}}>{t('jrs_emails')}</label>
            <textarea readOnly value={emails} rows={3} style={{fontSize:12,fontFamily:'var(--font-mono)',resize:'vertical'}}/>
          </div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <span style={{fontSize:11,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.06em'}}>
              {t('jrs_status')} — {confirmed}/{list.length} {t('jrs_confirmed')}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={load}>🔄 {t('jrs_refresh')}</button>
          </div>
          <div style={{maxHeight:280,overflowY:'auto',border:'1px solid var(--border)',borderRadius:'var(--radius)'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'var(--bg2)'}}>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{t('jrs_family')}</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{t('jrs_parent')}</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{t('jrs_email')}</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{t('jrs_status')}</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>{t('jrs_date')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map(f => (
                  <tr key={f.id} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'6px 10px'}}>{f.name}</td>
                    <td style={{padding:'6px 10px'}}>{[f.parent_first_name, f.parent_last_name].filter(Boolean).join(' ') || f.parent_name}</td>
                    <td style={{padding:'6px 10px',fontFamily:'var(--font-mono)',fontSize:11}}>{f.email||'—'}</td>
                    <td style={{padding:'6px 10px'}}>
                      {f.jrs_confirmed
                        ? <span className="badge badge-green">✅ {t('jrs_badgeConfirmed')}</span>
                        : <span className="badge badge-amber">⏳ {t('jrs_badgePending')}</span>}
                    </td>
                    <td style={{padding:'6px 10px',fontSize:11,color:'var(--muted)'}}>{f.jrs_date||'—'}</td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr><td colSpan={5} style={{textAlign:'center',padding:20,color:'var(--muted)'}}>{t('error')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {loading && <div style={{textAlign:'center',padding:20,color:'var(--muted)'}}>Laden…</div>}
      <div style={{display:'flex',justifyContent:'space-between',marginTop:20}}>
        <button className="btn btn-ghost" onClick={handleInitiate}>🆕 {t('jrs_new')}</button>
        <button className="btn btn-ghost" onClick={onClose}>Schließen</button>
      </div>
    </Modal>
  );
}

export default function Sidebar({ counts = {}, pinned = true, onTogglePin, mobileOpen = false, onMobileClose }) {
  const { user, logout, isAdmin, isParent } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showJrs, setShowJrs] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = (user?.fullName || user?.username || '?').slice(0,2).toUpperCase();

  const handleBackup = async () => {
    try {
      const res = await exportBackup();
      const url = URL.createObjectURL(new Blob([res.data], {type:'application/json'}));
      const a = document.createElement('a');
      const date = new Date().toISOString().slice(0,10);
      a.href = url;
      a.download = `maktab-backup-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('backup'));
    } catch { toast.error(t('error')); }
  };

  return (
    <aside className={`sidebar${pinned ? '' : ' sidebar-floating'}${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-logo">
        <h1>Maktab</h1>
        <p>Schülerverwaltung</p>
      </div>

      {NAV.map(section => {
        const visible = section.items.filter(i => i.roles.includes(user?.role));
        if (!visible.length) return null;
        return (
          <div className="nav-section" key={section.sectionKey}>
            <div className="nav-label">{t(section.sectionKey)}</div>
            {visible.map(item => (
              <NavLink
                key={item.to} to={item.to}
                onClick={onMobileClose}
                className={({isActive}) => `nav-item ${isActive?'active':''}`}
              >
                <item.icon size={16} strokeWidth={1.8}/>
                <span>{t(item.labelKey)}</span>
                {item.to==='/waiting' && counts.waiting>0 && (
                  <span className="badge">{counts.waiting}</span>
                )}
                {item.to==='/students' && counts.students>0 && (
                  <span className="badge">{counts.students}</span>
                )}
                {item.to==='/antraege' && counts.antraege>0 && (
                  <span className="badge" style={{background:'#f59e0b'}}>{counts.antraege}</span>
                )}
              </NavLink>
            ))}
          </div>
        );
      })}

      <div className="sidebar-footer">
        <div className="user-card">
          <div className="user-avatar">{initials}</div>
          <div>
            <div className="user-name">{user?.fullName || user?.username}</div>
            <div className="user-role">{user?.role}</div>
          </div>
        </div>
        <div style={{display:'flex',gap:4,marginBottom:8,alignItems:'center'}}>
          {isAdmin && (
            <button
              title="Jahresrundschreiben"
              onClick={() => setShowJrs(true)}
              style={{flex:1,padding:'5px 4px',background:'rgba(255,255,255,.08)',border:'none',borderRadius:'var(--radius)',color:'rgba(245,240,232,.7)',cursor:'pointer',fontSize:14,transition:'all .14s'}}
              onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,.15)'}
              onMouseOut={e=>e.currentTarget.style.background='rgba(255,255,255,.08)'}
            >📧</button>
          )}
          {isAdmin && (
            <button
              title={t('backup')}
              onClick={handleBackup}
              style={{flex:1,padding:'5px 4px',background:'rgba(255,255,255,.08)',border:'none',borderRadius:'var(--radius)',color:'rgba(245,240,232,.7)',cursor:'pointer',fontSize:14,transition:'all .14s'}}
              onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,.15)'}
              onMouseOut={e=>e.currentTarget.style.background='rgba(255,255,255,.08)'}
            >💾</button>
          )}
          <button
            className="sidebar-pin-btn"
            title={pinned ? 'Sidebar einklappen' : 'Sidebar anheften'}
            onClick={onTogglePin}
          >
            {pinned ? <PanelLeftClose size={14}/> : <PanelLeft size={14}/>}
          </button>
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          <LogOut size={13} style={{marginRight:6,verticalAlign:'middle'}}/>
          {t('logout')}
        </button>
      </div>

      {showJrs && <JrsModal onClose={() => setShowJrs(false)}/>}
    </aside>
  );
}
